import React, { useState, useEffect } from 'react';
import Alert from '../../common/Alert';
import commissionPaymentService from '../../../services/ShipmentCommissionPaymentService';
import { X } from 'lucide-react';

const AddPaymentModal = ({ commissionId, commission, hubPaymentData, onClose, onSuccess }) => {
  const senderHubs = Array.isArray(commission?.sender_hub_commissions) ? commission.sender_hub_commissions : [];
  const receiverHubs = Array.isArray(commission?.receiver_hub_commissions) ? commission.receiver_hub_commissions : [];

  const getInitialType = () => {
    if (hubPaymentData?.hub_type === 'sender') return 'SENDER_HUB';
    if (hubPaymentData?.hub_type === 'receiver') return 'RECEIVER_HUB';
    return 'VEHICLE_OWNER';
  };

  const getInitialRecipientId = () => {
    if (hubPaymentData?.hub_id) return hubPaymentData.hub_id;
    return commission?.vehicle_owner_id || '';
  };

  const [formData, setFormData] = useState({
    recipient_type:  getInitialType(),
    recipient_id:    getInitialRecipientId(),
    amount:          '',
    payment_date:    new Date().toISOString().split('T')[0],
    payment_mode:    'CASH',
    transaction_ref: '',
    remarks:         '',
  });
  const [fileAttachment, setFileAttachment] = useState(null);
  const [filePreview, setFilePreview]       = useState(null);
  const [loading, setLoading]               = useState(false);

  const paymentModes = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE', 'ONLINE'];

  const getBalance = (type, recipientId = null) => {
    if (type === 'VEHICLE_OWNER') {
      return parseFloat(commission?.vehicle_owner_commission_amount || 0)
           - parseFloat(commission?.vehicle_owner_paid_amount || 0);
    }
    if (type === 'COMPANY') {
      return parseFloat(commission?.company_commission_amount || 0)
           - parseFloat(commission?.company_paid_amount || 0);
    }
    if (type === 'SENDER_HUB' && recipientId) {
      const hub = senderHubs.find(h => h.hub_id === recipientId);
      return hub ? parseFloat(hub.hub_commission_amount || 0) - parseFloat(hub.paid_amount || 0) : 0;
    }
    if (type === 'RECEIVER_HUB' && recipientId) {
      const hub = receiverHubs.find(h => h.hub_id === recipientId);
      return hub ? parseFloat(hub.hub_commission_amount || 0) - parseFloat(hub.paid_amount || 0) : 0;
    }
    return 0;
  };

  const currentBalance = getBalance(formData.recipient_type, formData.recipient_id);

  const getRecipientName = () => {
    if (formData.recipient_type === 'VEHICLE_OWNER') return commission?.vehicle_owner_id || '';
    if (formData.recipient_type === 'COMPANY') return commission?.company_profile_id || '';
    if (formData.recipient_type === 'SENDER_HUB' && formData.recipient_id) {
      const hub = senderHubs.find(h => h.hub_id === formData.recipient_id);
      return hub ? `Sender Hub - ${hub.hub_location}` : formData.recipient_id;
    }
    if (formData.recipient_type === 'RECEIVER_HUB' && formData.recipient_id) {
      const hub = receiverHubs.find(h => h.hub_id === formData.recipient_id);
      return hub ? `Receiver Hub - ${hub.hub_location}` : formData.recipient_id;
    }
    return '';
  };

  const handleTypeSelect = (type) => {
    let recipientId = '';
    if (type === 'VEHICLE_OWNER') recipientId = commission?.vehicle_owner_id || '';
    else if (type === 'COMPANY') recipientId = commission?.company_profile_id || '';
    setFormData(prev => ({ ...prev, recipient_type: type, recipient_id: recipientId, amount: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { Alert.error('File must be under 5 MB'); return; }
    setFileAttachment(file);
    if (file.type.startsWith('image/')) {
      const r = new FileReader();
      r.onloadend = () => setFilePreview(r.result);
      r.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setFileAttachment(null);
    setFilePreview(null);
    const el = document.getElementById('add-pm-file');
    if (el) el.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if ((formData.recipient_type === 'SENDER_HUB' || formData.recipient_type === 'RECEIVER_HUB') && !formData.recipient_id) {
      Alert.error('Please select a hub'); return;
    }
    if (parseFloat(formData.amount) > currentBalance) {
      Alert.error(`Amount exceeds remaining balance of ₹${currentBalance.toLocaleString('en-IN')}`); return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('commission_id',   commissionId);
      fd.append('recipient_type',  formData.recipient_type);
      fd.append('recipient_id',    formData.recipient_id);
      fd.append('recipient_name',  getRecipientName());
      fd.append('amount',          formData.amount);
      fd.append('payment_date',    formData.payment_date);
      fd.append('payment_mode',    formData.payment_mode);
      if (formData.transaction_ref) fd.append('transaction_ref', formData.transaction_ref);
      if (formData.remarks)         fd.append('remarks',         formData.remarks);
      if (fileAttachment)           fd.append('file_attachment',  fileAttachment);

      await commissionPaymentService.createPayment(fd);
      Alert.success('Payment recorded successfully!');
      onSuccess();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const typeCards = [
    {
      type:  'VEHICLE_OWNER',
      label: 'Vehicle Owner',
      sub:   commission?.vehicle_owner_id,
      bal:   getBalance('VEHICLE_OWNER'),
      color: 'blue',
    },
    {
      type:  'COMPANY',
      label: 'Company',
      sub:   commission?.company_profile_id,
      bal:   getBalance('COMPANY'),
      color: 'green',
    },
    {
      type:  'SENDER_HUB',
      label: 'Sender Hub',
      sub:   `${senderHubs.length} hub${senderHubs.length !== 1 ? 's' : ''}`,
      bal:   senderHubs.reduce((s, h) => s + getBalance('SENDER_HUB', h.hub_id), 0),
      color: 'purple',
    },
    {
      type:  'RECEIVER_HUB',
      label: 'Receiver Hub',
      sub:   `${receiverHubs.length} hub${receiverHubs.length !== 1 ? 's' : ''}`,
      bal:   receiverHubs.reduce((s, h) => s + getBalance('RECEIVER_HUB', h.hub_id), 0),
      color: 'indigo',
    },
  ];

  const renderHubSelector = (hubs, hubType) => {
    if (!hubs || hubs.length === 0) return <p className="text-sm text-gray-500">No {hubType} hubs available</p>;
    return (
      <div>
        <label className="block text-sm font-medium text-secondary mb-2">
          Select Hub <span className="text-red-500">*</span>
        </label>
        <select
          name="recipient_id"
          value={formData.recipient_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm"
          required
        >
          <option value="">Select Hub</option>
          {hubs.map(h => {
            const bal = parseFloat(h.hub_commission_amount || 0) - parseFloat(h.paid_amount || 0);
            return (
              <option key={h.hub_id} value={h.hub_id} disabled={bal <= 0}>
                {h.hub_location} ({h.hub_id}) — Balance ₹{bal.toLocaleString('en-IN')} ({h.payment_status || 'PENDING'})
              </option>
            );
          })}
        </select>

        {formData.recipient_id && (() => {
          const hub = hubs.find(h => h.hub_id === formData.recipient_id);
          if (!hub) return null;
          return (
            <div className="mt-2 grid grid-cols-3 gap-2">
              <div className="bg-brand-light border border-blue-200 rounded-sm p-2 text-center">
                <p className="text-xs text-brand">Shipments</p>
                <p className="font-bold text-sm">{hub.hub_total_shipments || 0}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-2 text-center">
                <p className="text-xs text-yellow-600">Total Freight</p>
                <p className="font-bold text-sm">₹{parseFloat(hub.hub_total_freight || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-sm p-2 text-center">
                <p className="text-xs text-green-600">Commission</p>
                <p className="font-bold text-sm">₹{parseFloat(hub.hub_commission_amount || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-secondary">Add Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Type selector */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Pay To <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {typeCards.map(({ type, label, sub, bal, color }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeSelect(type)}
                  className={`p-3 border-2 rounded-sm text-left transition-all ${
                    formData.recipient_type === type
                      ? `border-${color}-500 bg-${color}-50`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-semibold text-sm text-secondary">{label}</p>
                  {sub && <p className="text-xs text-gray-500 mt-0.5 truncate">{sub}</p>}
                  <p className={`text-xs font-bold mt-1 ${bal > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                    Bal: ₹{bal.toLocaleString('en-IN')}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Hub selector */}
          {formData.recipient_type === 'SENDER_HUB' && renderHubSelector(senderHubs, 'sender')}
          {formData.recipient_type === 'RECEIVER_HUB' && renderHubSelector(receiverHubs, 'receiver')}

          {/* Amount + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Remaining: <span className="font-semibold text-blue-600">₹{currentBalance.toLocaleString('en-IN')}</span>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                required
              />
            </div>
          </div>

          {/* Mode + Ref */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Payment Mode <span className="text-red-500">*</span>
              </label>
              <select
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
              >
                {paymentModes.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Transaction Reference</label>
              <input
                type="text"
                name="transaction_ref"
                value={formData.transaction_ref}
                onChange={handleChange}
                placeholder="UTR / Cheque no."
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows="2"
              placeholder="Optional remarks..."
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          {/* File */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Payment Proof (Optional)</label>
            <input
              id="add-pm-file"
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <p className="text-xs text-gray-500 mt-1">Images or PDF, max 5 MB</p>
            {filePreview && (
              <div className="mt-3 relative inline-block">
                <img src={filePreview} alt="Preview" className="w-28 h-28 object-cover border rounded-md" />
                <button type="button" onClick={removeFile}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">×</button>
              </div>
            )}
            {fileAttachment && !filePreview && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                📄 {fileAttachment.name}
                <button type="button" onClick={removeFile} className="text-red-600 text-xs">Remove</button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 font-medium disabled:opacity-50 transition-colors">
              {loading ? 'Saving...' : 'Add Payment'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 font-medium transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaymentModal;
